import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera,
  CheckCircle2,
  ImagePlus,
  Loader2,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const ACCEPTED_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * Premium, non-technical staff ID photo collection journey.
 *
 * Designed for people who are not confident with computers: one large obvious
 * action at a time, minimal words, friendly instructions, a clear preview,
 * visible progress and a success state that returns them to their tasks (never
 * a dead end). It reuses the canonical staff-ID photo endpoint — it does NOT
 * create a second photo field or a second identity.
 */
export default function StaffIdPhotoDialog({
  open,
  onOpenChange,
  apiBase,
  authHeaders,
  uploadPath = '/worker/staff-id/photo',
  prompt,
  hasExistingPhoto = false,
  brandColor = '#0F5C5E',
  onUploaded,
}) {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [step, setStep] = useState('choose'); // choose | preview | uploading | success
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [progress, setProgress] = useState(0);

  const maxMb = prompt?.max_mb || 5;
  const formats = prompt?.accepted_formats || ['JPG', 'PNG', 'WEBP'];
  const instructions = prompt?.instructions || [
    'Face the camera so your whole face is clearly visible',
    'Find somewhere with good, even lighting',
    'Use a plain, quiet background if you can',
    'No filters, sunglasses or hats',
    'Just you — one person only in the photo',
  ];

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    clearPreview();
    setSelectedFile(null);
    setPreviewUrl(null);
    setProgress(0);
    setStep('choose');
  }, [clearPreview]);

  // Reset whenever the dialog is (re)opened so the person always starts fresh.
  useEffect(() => {
    if (open) {
      resetState();
    }
    return clearPreview;
  }, [open, resetState, clearPreview]);

  const handleFilePicked = (event) => {
    const file = event.target.files?.[0];
    // Allow re-picking the same file next time.
    if (event.target) event.target.value = '';
    if (!file) return;

    if (!ACCEPTED_MIME.includes(file.type)) {
      toast.error(`Please choose a ${formats.join(', ')} photo.`);
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Please choose a photo smaller than ${maxMb}MB.`);
      return;
    }
    if (file.size === 0) {
      toast.error('That photo looks empty. Please try again.');
      return;
    }

    clearPreview();
    const objectUrl = URL.createObjectURL(file);
    previewUrlRef.current = objectUrl;
    setSelectedFile(file);
    setPreviewUrl(objectUrl);
    setStep('preview');
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setStep('uploading');
    setProgress(0);
    try {
      const body = new FormData();
      body.append('file', selectedFile);
      await axios.post(`${apiBase}${uploadPath}`, body, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.min(99, Math.round((evt.loaded / evt.total) * 100)));
          }
        },
      });
      setProgress(100);
      setStep('success');
      if (typeof onUploaded === 'function') {
        onUploaded();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sorry, we could not save your photo. Please try again.');
      setStep('preview');
    }
  };

  const close = () => onOpenChange?.(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl p-0 overflow-hidden"
        data-testid="staff-id-photo-dialog"
      >
        <div className="px-5 py-4 text-white" style={{ backgroundColor: brandColor }}>
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="text-lg font-semibold text-white">
              {prompt?.headline || 'Add your staff ID photo'}
            </DialogTitle>
            <DialogDescription className="text-white/85 text-sm">
              {step === 'success'
                ? 'All done — your photo is saved.'
                : 'This photo appears on your staff ID card. It takes less than a minute.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5">
          {/* Hidden inputs: one opens the camera (front-facing where supported),
              one opens the gallery/files. */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            capture="user"
            onChange={handleFilePicked}
            className="hidden"
            aria-label="Take a photo for your staff ID"
            data-testid="staff-id-photo-camera-input"
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFilePicked}
            className="hidden"
            aria-label="Choose an existing photo for your staff ID"
            data-testid="staff-id-photo-gallery-input"
          />

          {step === 'choose' && (
            <div className="space-y-4" data-testid="staff-id-photo-step-choose">
              <ul className="space-y-2">
                {instructions.map((line) => (
                  <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <div className="grid gap-3">
                <Button
                  type="button"
                  className="h-14 justify-center gap-2 rounded-xl text-base"
                  style={{ backgroundColor: brandColor }}
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="staff-id-photo-take"
                >
                  <Camera className="h-5 w-5" />
                  Take a photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-14 justify-center gap-2 rounded-xl text-base"
                  onClick={() => galleryInputRef.current?.click()}
                  data-testid="staff-id-photo-choose"
                >
                  <ImagePlus className="h-5 w-5" />
                  Choose a photo
                </Button>
              </div>

              <p className="text-center text-xs text-slate-400">
                {formats.join(', ')} · up to {maxMb}MB
              </p>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4" data-testid="staff-id-photo-step-preview">
              <div className="mx-auto h-56 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                <img
                  src={previewUrl || undefined}
                  alt="Your staff ID preview"
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="text-center text-sm text-slate-600">Happy with this photo?</p>
              <div className="grid gap-3">
                <Button
                  type="button"
                  className="h-12 justify-center gap-2 rounded-xl text-base"
                  style={{ backgroundColor: brandColor }}
                  onClick={handleUpload}
                  data-testid="staff-id-photo-confirm"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Use this photo
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 justify-center gap-2 rounded-xl text-slate-600"
                  onClick={() => { resetState(); }}
                  data-testid="staff-id-photo-retake"
                >
                  <RotateCcw className="h-4 w-4" />
                  Take or choose another
                </Button>
              </div>
            </div>
          )}

          {step === 'uploading' && (
            <div className="space-y-4 py-4 text-center" data-testid="staff-id-photo-step-uploading">
              <Loader2 className="mx-auto h-8 w-8 animate-spin" style={{ color: brandColor }} />
              <p className="text-sm font-medium text-slate-700">Saving your photo…</p>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-4 py-2 text-center" data-testid="staff-id-photo-step-success">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <ShieldCheck className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-base font-semibold text-slate-900">Your staff ID photo is saved</p>
                <p className="mt-1 text-sm text-slate-600">
                  {hasExistingPhoto
                    ? 'We have updated the photo on your staff ID.'
                    : 'It will appear on your digital and printed staff ID.'}
                </p>
              </div>
              <Button
                type="button"
                className="h-12 w-full justify-center rounded-xl text-base"
                style={{ backgroundColor: brandColor }}
                onClick={close}
                data-testid="staff-id-photo-done"
              >
                Back to my tasks
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
